import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { CowreMark, IconClose, IconPlus } from '../../../common/icons';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { formatDateOnly } from '../../../common/utils/date';
import { TE_ROLE_LABEL } from '../../../core/constants/transfers';
import { useOpuSession } from '../../opu/contexts/opu';
import { useAvailableEmbryos } from '../../embryos/contexts/embryos';
import { useCreateTransferSession, useOpuTransferSessions } from '../contexts/transfers';
import { transferKeys } from '../queries/keys';
import { transferPaths } from '../queries/paths';
import { buildTransferredRows, enrichRows, mergeBoardRows } from '../utils/board';
import TransferStartModal from '../forms/start-modal';
import TransferRows from '../forms/transfer-rows';
import BankPicker from '../forms/bank-picker';
import ResumeList from './resume-list';
import SessionLegend from './session-legend';

import './styles/workspace.css';

const today = () => new Date().toLocaleDateString('en-CA');

function buildRemarks(remarks) {
    const out = {};
    if (remarks?.corpusLuteum) out.corpusLuteum = remarks.corpusLuteum;
    if (remarks?.details) out.details = remarks.details;
    return Object.keys(out).length ? out : null;
}

export default function TransferWorkspace() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [params] = useSearchParams();
    const opuId = params.get('session');

    const { data: session, isLoading, error } = useOpuSession(opuId, 'standard');
    const { data: resume, isLoading: loadingResume } = useOpuTransferSessions(opuId);
    const { data: embryosData, error: embryosError } = useAvailableEmbryos({ opu: opuId, limit: 500 }, { enabled: !!opuId });
    const { mutateAsync: createSession, isPending } = useCreateTransferSession();

    // Detalle assembled de cada sesión TE de la OPU (el derivado no trae remarks
    // CL/OBS ni linaje) → hidrata el tablero para editar CL/Obs de transferencias
    // de cualquier sesión. Hook antes del early-return del landing (rules-of-hooks).
    const sessionIds = (resume?.transfer_sessions || []).map((s) => s.id);
    const sessionDetailQueries = useQueries({
        queries: sessionIds.map((id) => ({
            queryKey: transferKeys.detail(id),
            queryFn: ({ signal }) => api.get(transferPaths.session(id), { signal }).then((r) => r.data),
            enabled: !!id,
            staleTime: 30_000,
        })),
    });

    const [meta, setMeta] = useState(null);         // { farm, staff, date } del modal de inicio
    const [showStart, setShowStart] = useState(false);
    const [rows, setRows] = useState(null);
    const [seeded, setSeeded] = useState(false);
    const [showBank, setShowBank] = useState(false);
    const [hideTransferred, setHideTransferred] = useState(false);
    const confirm = useConfirm();

    if (embryosData && !seeded) {
        setRows((embryosData.items || []).map(e => ({ key: `e${e.id}`, embryo: e, source: 'opu', recipient: null, remarks: {} })));
        setSeeded(true);
    }

    if (isLoading || (!session && !error)) return <div className="transfer-ws__state">Cargando sesión…</div>;
    if (error || !session) return <div className="transfer-ws__state">Sesión OPU no encontrada.</div>;

    const list = rows || [];
    const assigned = list.filter(r => r.recipient);
    const cancel = async () => {
        if (assigned.length) {
            const ok = await confirm({
                title: 'Descartar transferencias',
                message: '¿Descartar las transferencias en progreso?',
                confirmLabel: 'Descartar',
                danger: true,
            });
            if (!ok) return;
        }
        navigate(`/admin/opu/${opuId}`);
    };

    // Al confirmar el modal de inicio: si cambió la FINCA, las receptoras ya
    // asignadas (de la finca anterior) quedan inválidas → se limpian (0023 §4).
    const confirmStart = (m) => {
        if (meta && meta.farm?.id !== m.farm?.id) {
            setRows(prev => (prev ? prev.map(r => ({ ...r, recipient: null })) : prev));
        }
        setMeta(m);
        setShowStart(false);
    };

    const topbar = (
        <header className="transfer-ws__topbar">
            <div className="transfer-ws__brand">
                <span className="transfer-ws__mark"><CowreMark size={30} id="te-mark" /></span>
                <span className="transfer-ws__brand-text">COWRE</span>
                <span className="transfer-ws__divider" />
                <span className="transfer-ws__title">Transferencia · {session.code}</span>
            </div>
            <button type="button" className="transfer-ws__close" onClick={cancel} aria-label="Cerrar"><IconClose /></button>
        </header>
    );

    // ---- LANDING: reanudar sesiones existentes o iniciar una nueva ----
    if (!meta) {
        return (
            <div className="transfer-ws">
                {topbar}
                <div className="transfer-ws__content">
                    <ResumeList
                        data={resume}
                        loading={loadingResume}
                        onResume={(te) => navigate(`/opu/transfer/edit?session=${te.id}&opu=${opuId}`)}
                    />
                    <button type="button" className="transfer-ws__new" onClick={() => setShowStart(true)}>
                        <IconPlus /><span>Nueva transferencia</span>
                    </button>
                </div>
                {showStart && (
                    <TransferStartModal onConfirm={confirmStart} onCancel={() => setShowStart(false)} />
                )}
            </div>
        );
    }

    // ---- GRID: asignar receptoras y datos clínicos ----
    const setRecipient = (key, rec) => setRows(prev => prev.map(r => (r.key === key ? { ...r, recipient: rec } : r)));
    const clearRecipient = (key) => setRows(prev => prev.map(r => (r.key === key ? { ...r, recipient: null } : r)));
    const setCL = (key, cl) => setRows(prev => prev.map(r => (r.key === key ? { ...r, remarks: { ...r.remarks, corpusLuteum: cl } } : r)));
    const setObs = (key, d) => setRows(prev => prev.map(r => (r.key === key ? { ...r, remarks: { ...r.remarks, details: d } } : r)));
    const removeRow = (key) => setRows(prev => prev.filter(r => r.key !== key));
    const addBank = (embryos) => setRows(prev => {
        const have = new Set(prev.map(r => r.embryo.id));
        const fresh = embryos.filter(e => !have.has(e.id));
        return [...prev, ...fresh.map(e => ({ key: `e${e.id}`, embryo: e, source: 'bank', recipient: null, remarks: {} }))];
    });

    const byId = new Map();
    sessionDetailQueries.forEach((q) => (q.data?.transfers || []).forEach((t) => byId.set(t.id, t)));
    const transferredRows = enrichRows(buildTransferredRows(resume), byId);
    const display = mergeBoardRows(list, hideTransferred ? [] : transferredRows);
    const existingIds = new Set([...list.map(r => r.embryo.id), ...transferredRows.map(r => r.embryo.id)]);

    // Deshacer/corregir una transferencia YA registrada de otra sesión de la OPU
    // (el técnico pudo equivocarse de sesión). Solo si la preñez está `open`
    // (422/409 si progresó). Opera contra la sesión dueña de la fila.
    const invalidateBoard = () => {
        qc.invalidateQueries({ queryKey: transferKeys.all });
        if (opuId) qc.invalidateQueries({ queryKey: ['transfer-sessions', 'opu', opuId] });
        qc.invalidateQueries({ queryKey: ['opu'] });
    };
    const patchTransfer = async (row, body, okMsg) => {
        try {
            await api.patch(transferPaths.transferItem(row.sessionId, row.transferId), body);
            invalidateBoard();
            if (okMsg) toast.success(okMsg);
        } catch (err) {
            if (err?.status === 422) toast.error(err?.message || 'No editable: la preñez ya progresó');
            else if (err?.status === 409) toast.error(err?.message || 'Conflicto al editar la transferencia');
            else toast.error(err?.message || 'No se pudo editar la transferencia');
        }
    };
    const editRecipient = (row, rec) => patchTransfer(row, { recipient_id: rec.id }, 'Receptora actualizada');
    const editRemarks = (row, remarks) => patchTransfer(row, { remarks });
    const deleteTransfer = async (row) => {
        const ok = await confirm({
            title: 'Deshacer transferencia',
            message: `¿Deshacer la transferencia de ${row.embryo?.code || 'este embrión'}?`,
            confirmLabel: 'Deshacer',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(transferPaths.transferItem(row.sessionId, row.transferId));
            invalidateBoard();
            toast.success('Transferencia deshecha');
        } catch (err) {
            if (err?.status === 409) toast.error('No se puede deshacer: la preñez ya progresó');
            else toast.error(err?.message || 'No se pudo deshacer la transferencia');
        }
    };

    const changeSession = async () => {
        if (assigned.length) {
            const ok = await confirm({
                title: 'Descartar transferencia',
                message: '¿Descartar esta transferencia y volver al menú de sesiones?',
                confirmLabel: 'Descartar',
                danger: true,
            });
            if (!ok) return;
        }
        setMeta(null);
        setRows(null);          // descarta de verdad: re-seedea al volver a entrar
        setSeeded(false);
        setHideTransferred(false);
        setShowBank(false);
    };

    const handleSave = async () => {
        if (!assigned.length) { toast.error('Asigná al menos una receptora'); return; }
        const transfers = assigned.map(r => {
            const remarks = buildRemarks(r.remarks);
            return { embryo_id: r.embryo.id, recipient_id: r.recipient.id, ...(remarks ? { remarks } : {}) };
        });
        const payload = {
            date: meta.date || today(),
            farm_id: meta.farm.id,
            ...(meta.staff.length ? { staff: meta.staff.map(s => ({ user_id: s.user.id, role: s.role })) } : {}),
            transfers,
        };
        try {
            const created = await createSession(payload);
            toast.success(`${transfers.length} transferencia(s) registradas`);
            navigate(`/admin/transfer/${created.id}${opuId ? `?opu=${opuId}` : ''}`);
        } catch (err) {
            if (err?.status === 409) toast.error(err?.message || 'Un embrión ya fue transferido. Recargá la pestaña: la lista de embriones puede estar desactualizada.');
            else if (err?.status === 422) toast.error(err?.message || 'Revisá las receptoras');
            else toast.error(err?.message || 'No se pudo registrar la transferencia');
        }
    };

    return (
        <div className="transfer-ws">
            {topbar}

            <div className="transfer-ws__content">
                {embryosError && (
                    <div className="transfer-ws__error">
                        {embryosError.status === 403
                            ? 'No tenés permiso para ver los embriones (embryos:read). No se pudieron cargar los de esta OPU.'
                            : 'No se pudieron cargar los embriones de esta OPU. Recargá la página o probá agregar del banco.'}
                    </div>
                )}

                <section className="transfer-ws__summary">
                    <div className="transfer-ws__summary-main">
                        <span className="transfer-summary__farm">{meta.farm.name}{meta.farm.owner?.name ? ` · ${meta.farm.owner.name}` : ''}</span>
                        <span className="transfer-summary__sub">
                            {formatDateOnly(meta.date || today())}
                            {meta.staff.length > 0 && ` · ${meta.staff.map(s => `${s.user.name} (${TE_ROLE_LABEL[s.role]})`).join(', ')}`}
                        </span>
                    </div>
                    <div className="transfer-ws__summary-actions">
                        <button type="button" className="transfer-ws__switch" onClick={changeSession} disabled={isPending}>Cambiar sesión</button>
                        <button type="button" className="transfer-ws__bank" onClick={() => setShowBank(true)} disabled={isPending}>
                            <IconPlus /><span>Agregar del banco</span>
                        </button>
                        <button type="button" className="transfer-ws__change" onClick={() => setShowStart(true)} disabled={isPending}>Cambiar datos</button>
                    </div>
                </section>

                <section className="transfer-ws__meta">
                    <span>{assigned.length}/{list.length} receptora(s) asignada(s){transferredRows.length ? ` · ${transferredRows.length} transferida(s)` : ''}</span>
                    {transferredRows.length > 0 && (
                        <label className="transfer-ws__toggle">
                            <input type="checkbox" checked={hideTransferred} onChange={(e) => setHideTransferred(e.target.checked)} />
                            <span>Ocultar transferidas</span>
                        </label>
                    )}
                </section>

                {!hideTransferred && transferredRows.length > 0 && <SessionLegend sessions={resume?.transfer_sessions} />}

                {!display.length ? (
                    <div className="transfer-ws__state">No hay embriones de esta OPU. Podés agregar del banco.</div>
                ) : (
                    <TransferRows
                        rows={display}
                        farmId={meta.farm.id}
                        onRecipient={setRecipient}
                        onClearRecipient={clearRecipient}
                        onCL={setCL}
                        onObs={setObs}
                        onRemove={removeRow}
                        onEditRecipient={editRecipient}
                        onEditRemarks={editRemarks}
                        onDeleteTransfer={deleteTransfer}
                    />
                )}
            </div>

            <footer className="transfer-ws__footer">
                <span className="transfer-ws__footer-hint">{assigned.length} transferencia(s) lista(s)</span>
                <div className="transfer-ws__actions">
                    <button type="button" className="transfer-ws__cancel" onClick={cancel} disabled={isPending}>Cancelar</button>
                    <button type="button" className="transfer-ws__primary" onClick={handleSave} disabled={isPending || !assigned.length}>
                        {isPending ? 'Guardando…' : 'Registrar transferencias'}
                    </button>
                </div>
            </footer>

            {showBank && <BankPicker existingIds={existingIds} onAdd={addBank} onClose={() => setShowBank(false)} />}
            {showStart && (
                <TransferStartModal onConfirm={confirmStart} onCancel={() => setShowStart(false)} />
            )}
        </div>
    );
}
